/**
 * Phase 6b1.5 PR 2 — `<LocationModal>`.
 *
 * Two stacked blocks: a primary Detect CTA card + a collapsed
 * manual-entry block ("Or enter manually ›"). Atomic 4-key save
 * through `useSettingsPersist.saveFields`:
 *
 *   `_city` · `_latitude` · `_longitude` · `_vitamin_d_risk`
 *
 * All geocoding routes through `lib/locationGeocode.ts` — which since
 * 2026-08-16 (§8.3) talks ONLY to our own `/api/geocode` and
 * `/api/reverse-geocode`, never to a third-party geocoder from the
 * device. The wrapper exposes only reverse + single-best-forward, NO
 * autocomplete; the modal explicitly does NOT render any dropdown as
 * the user types in the manual input.
 *
 * ⚖️ §8.2 — COORDINATES ARE ROUNDED AT CAPTURE, not at save. Both
 * `handleDetect` and `handleSave` pass every coordinate through
 * `roundToCityLevel` (2 dp ≈ 1.1 km) the moment it arrives, so the
 * precise fix is never held in component state, never rendered in the
 * detected-coords line, and never handed to `onSave`. Rounding later
 * would be strictly weaker — everything upstream would already have
 * seen the real number. `settingsPersist` rounds again at validate
 * time; that is a backstop, not the control.
 *
 * Detect button flow:
 *   1. `Detecting…` chip pulses
 *   2. On success → `Looking up address…`, fires ONE reverseGeocode
 *   3. On reverse success → `Location detected` + detected summary
 *   4. On reverse failure → `Address lookup failed, coordinates saved`
 *   5. On permission denied → `Location access denied`
 *   6. On 10s geolocation timeout → `Couldn't detect location · try manual entry`
 *
 * Save flow:
 *   - If Detect populated coords → save the cached result (4-key)
 *   - If manual entry only → fire ONE geocodeCityOnSave forward call
 *     - On success → 4-key save
 *     - On failure → 2-key save (`_city` + null coords/risk)
 *
 * Default focus = Cancel (per safety gate #7 from PR 9).
 * ESC + backdrop click = dismiss without save.
 * Focus trap inside modal.
 *
 * The OSM attribution that used to render in the footer is REMOVED
 * with the geocoder it credited (§8.3). Apple's Maps Server API needs
 * no visible credit for a geocode that draws no map — the run map's
 * own Apple mark is automatic, and REL-8's marks cover the surfaces
 * that render Apple DATA.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useKeyboardReveal } from '../../lib/formKeyboard';
import {
  geocodeCityOnSave,
  reverseGeocode,
  roundToCityLevel,
  vitaminDRiskFromLatitude,
  type VitaminDRiskBand,
} from '../../lib/locationGeocode';
import type { SettingKey } from '../../lib/settingsPersist';
import styles from './LocationModal.module.css';

type DetectStatus =
  | { kind: 'idle' }
  | { kind: 'detecting' }
  | { kind: 'looking-up'; lat: number; lon: number }
  | { kind: 'ok'; city: string; lat: number; lon: number }
  | { kind: 'warn-geocode-failed'; lat: number; lon: number }
  | { kind: 'err-permission' }
  | { kind: 'warn-timeout' };

type SaveStatus = 'idle' | 'saving' | 'error';

export interface LocationModalProps {
  /** Pre-fill manual input from the existing persisted city. */
  initialCity?: string | null;
  /** Atomic 4-key save. Returns a promise that resolves on success
   *  (modal closes) or rejects on failure (modal stays open + error chip). */
  onSave: (updates: Partial<Record<SettingKey, unknown>>) => Promise<void>;
  /** Cancel / ESC / backdrop click. */
  onClose: () => void;
}

const COPY = {
  detecting: 'Detecting…',
  lookingUp: 'Looking up address…',
  ok: 'Location detected',
  warnGeocodeFailed: 'Address lookup failed, coordinates saved',
  errPermission: 'Location access denied',
  warnTimeout: "Couldn't detect location · try manual entry",
  saveError: "Couldn't save · retry",
};

export function LocationModal({ initialCity, onSave, onClose }: LocationModalProps) {
  const isMobile = !useMediaQuery('(min-width: 1024px)');
  const isSheet = isMobile;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // B51 — the mobile sheet is bottom-anchored (`align-items: flex-end`,
  // LocationModal.module.css), the exact band `KeyboardResize.None` leaves
  // the keyboard overlaying. Same shared helper as JoinTeamModal/
  // RaceFormFields; desktop is gated out inside the hook.
  const keyboard = useKeyboardReveal();

  const [detect, setDetect] = useState<DetectStatus>({ kind: 'idle' });
  const [manualExpanded, setManualExpanded] = useState<boolean>(false);
  const [manualCity, setManualCity] = useState<string>(initialCity ?? '');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  // Cancel default focus on mount (NEVER Save).
  useEffect(() => {
    const t = setTimeout(() => cancelRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  // ESC dismiss + focus trap.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  // ── Detect flow ─────────────────────────────────────────────

  const handleDetect = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setDetect({ kind: 'err-permission' });
      return;
    }
    setDetect({ kind: 'detecting' });
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        // ⚖️ §8.2 — ROUNDED HERE, at the device boundary. `pos.coords` is the
        // only place a precise fix exists in this component, and it ends on
        // these two lines: everything below — state, the detected-coords line,
        // the geocode request, the save — sees city level only.
        const lat = roundToCityLevel(pos.coords.latitude);
        const lon = roundToCityLevel(pos.coords.longitude);
        setDetect({ kind: 'looking-up', lat, lon });
        try {
          const result = await reverseGeocode(lat, lon);
          if (result.city) {
            setDetect({ kind: 'ok', city: result.displayName, lat, lon });
            // Sync the manual input so a subsequent Save reads the
            // detected city without forcing manualBlock expansion.
            setManualCity(result.displayName);
          } else {
            setDetect({ kind: 'warn-geocode-failed', lat, lon });
            setManualCity(result.displayName); // coords-only fallback string
          }
        } catch {
          setDetect({ kind: 'warn-geocode-failed', lat, lon });
        }
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setDetect({ kind: 'err-permission' });
        } else if (err.code === err.TIMEOUT) {
          setDetect({ kind: 'warn-timeout' });
        } else {
          setDetect({ kind: 'warn-timeout' });
        }
      },
      { timeout: 10_000, maximumAge: 60_000 },
    );
  }, []);

  // ── Save flow ───────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    setSaveStatus('saving');
    // Resolve the 4 keys from the current state.
    let city: string | null = null;
    let lat: number | null = null;
    let lon: number | null = null;
    let vitd: VitaminDRiskBand | null = null;

    // Detect-populated path: use cached coords from the detect step.
    if (
      detect.kind === 'ok' ||
      detect.kind === 'warn-geocode-failed'
    ) {
      lat = detect.lat;
      lon = detect.lon;
      vitd = vitaminDRiskFromLatitude(detect.lat);
      // Prefer the typed manual override if the athlete tweaked it.
      city = manualCity.trim() || (detect.kind === 'ok' ? detect.city : null);
    } else if (manualCity.trim() !== '') {
      // Manual-only path: fire ONE forward geocode.
      const query = manualCity.trim();
      const match = await geocodeCityOnSave(query).catch(() => null);
      if (match) {
        city = match.label;
        // §8.2 — the geocoder answers with a city centroid, which is already
        // coarse; round anyway so every stored pair has the same shape and no
        // provider's precision leaks through this path.
        lat = roundToCityLevel(match.lat);
        lon = roundToCityLevel(match.lon);
        // ⚠️ Derived from the ROUNDED latitude, not `match.lat`. Within ~550 m
        // of the 35°/45° edge the two disagree, and storing a band the stored
        // coordinate does not imply is a row nobody can later explain.
        vitd = vitaminDRiskFromLatitude(lat);
      } else {
        // Save city without coords; VitD stays omitted.
        city = query;
      }
    } else {
      // Nothing to save — close.
      onClose();
      return;
    }

    try {
      await onSave({
        _city: city,
        _latitude: lat,
        _longitude: lon,
        _vitamin_d_risk: vitd,
      });
      // Success — caller closes the modal.
    } catch {
      setSaveStatus('error');
    }
  }, [detect, manualCity, onSave, onClose]);

  // ── Detect status visual ────────────────────────────────────

  const detectStatusVisual: { text: string; state: string } | null = (() => {
    switch (detect.kind) {
      case 'idle':
        return null;
      case 'detecting':
        return { text: COPY.detecting, state: 'detecting' };
      case 'looking-up':
        return { text: COPY.lookingUp, state: 'looking-up' };
      case 'ok':
        return { text: COPY.ok, state: 'ok' };
      case 'warn-geocode-failed':
        return { text: COPY.warnGeocodeFailed, state: 'warn' };
      case 'err-permission':
        return { text: COPY.errPermission, state: 'err' };
      case 'warn-timeout':
        return { text: COPY.warnTimeout, state: 'warn' };
    }
  })();

  const canSave =
    detect.kind === 'ok' ||
    detect.kind === 'warn-geocode-failed' ||
    manualCity.trim().length >= 3;

  // B51 — portalled to `document.body`, same as `WaiverModal` (B52b). This
  // modal renders inside `<SettingsSection>`, whose card carries
  // `content-visibility: auto` (SettingsSection.module.css) — paint
  // containment makes the card the containing block for `position: fixed`
  // descendants and clips them to it. Build 32 lifted the sheet clear of
  // the keyboard but never moved it out of the card, so it still failed on
  // the founder's phone. Portalling out from under the ancestor is what
  // makes that keyboard lift (and the sheet's own safe-area/z-index CSS)
  // meaningful at all.
  const overlay = (
    <div
      className={isSheet ? styles.sheetBackdrop : styles.modalBackdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={isSheet ? styles.sheetContainer : styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-modal-title"
      >
        {isSheet && <div className={styles.handle} aria-hidden="true" />}
        <header className={styles.header}>
          <h2 id="location-modal-title" className={styles.title}>
            Update Training Location
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </header>

        <div className={styles.body}>
          {/* Detect block — primary CTA */}
          <div className={styles.detectBlock}>
            <div className={styles.detectTitle}>Use your device location</div>
            <div className={styles.detectSub}>
              One reverse-geocode call: your device shares its coordinates once. No
              continuous tracking.
            </div>
            <button
              type="button"
              className={styles.detectBtn}
              onClick={handleDetect}
              disabled={detect.kind === 'detecting' || detect.kind === 'looking-up'}
            >
              <span aria-hidden="true">◉</span> Detect My Location
            </button>
            {detectStatusVisual && (
              <div
                className={styles.detectStatus}
                data-state={detectStatusVisual.state}
                role="status"
                aria-live="polite"
              >
                <span className={styles.dot} aria-hidden="true" />
                {detectStatusVisual.text}
              </div>
            )}
            {/* Detected-summary block — shows the resolved city +
             *  coords BEFORE the athlete commits via Save. Fixes the
             *  PR 2 smoke nit where the user couldn't see what got
             *  detected until after closing the modal. */}
            {(detect.kind === 'ok' || detect.kind === 'warn-geocode-failed') && (
              <div className={styles.detectedSummary}>
                <div className={styles.detectedCity}>
                  {detect.kind === 'ok' ? detect.city : 'Address not found · saving coordinates only'}
                </div>
                {/* §8.2 — 2 dp, because 2 dp is what gets stored. Showing
                    three decimals of a value we deliberately coarsened would
                    tell the athlete we keep a precision we do not. */}
                <div className={styles.detectedCoords}>
                  {detect.lat.toFixed(2)}°{detect.lat >= 0 ? 'N' : 'S'} ·{' '}
                  {detect.lon.toFixed(2)}°{detect.lon >= 0 ? 'E' : 'W'}
                </div>
              </div>
            )}
          </div>

          {/* Manual block — collapsed by default */}
          <button
            type="button"
            className={styles.manualToggle}
            onClick={() => setManualExpanded((v) => !v)}
            aria-expanded={manualExpanded}
          >
            Or enter manually {manualExpanded ? '▾' : '›'}
          </button>
          {manualExpanded && (
            <div className={styles.manualBlock}>
              <label className={styles.manualLabel} htmlFor="loc-manual-city">
                Your city
              </label>
              <input
                id="loc-manual-city"
                type="text"
                className={styles.manualInput}
                placeholder="e.g. Boulder, CO · Tokyo, Japan · Bristol, UK"
                value={manualCity}
                onChange={(e) => setManualCity(e.target.value)}
                onFocus={keyboard.onFocus}
                onBlur={keyboard.onBlur}
                autoComplete="off"
              />
              {/* Honest UX note explaining the no-autocomplete
               *  constraint. It began as OSM policy; post-§8.3 it stays
               *  because a keystroke-rate geocoder is a keystroke-rate
               *  disclosure. ONE lookup on Save instead. International
               *  cities are fine — free-text format. */}
              <div className={styles.manualHelp}>
                Free text: city + region or country (we'll look it up on Save).
              </div>
            </div>
          )}
        </div>

        {/* ⚖️ §8.3 — the OSM credit is GONE because the OSM geocoder is gone.
            An attribution naming a party we no longer send anything to is not
            merely stale copy; it is a false statement about where the athlete's
            coordinates went.
            The footer is `space-between` and the credit was its left child, so
            deleting it alone would have pulled the actions left. `.actions`
            takes `margin-left: auto` instead — an empty <span> propping a
            layout open is a phantom the next person deletes without knowing
            what it was for. */}
        <footer className={styles.footer}>
          <div className={styles.actions}>
            {saveStatus === 'error' && (
              <span className={styles.saveError} role="alert">
                {COPY.saveError}
              </span>
            )}
            <button
              ref={cancelRef}
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={saveStatus === 'saving'}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={!canSave || saveStatus === 'saving'}
              data-state={saveStatus}
            >
              {saveStatus === 'saving' ? 'Saving…' : 'Save'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );

  return typeof document !== 'undefined' && document.body
    ? createPortal(overlay, document.body)
    : overlay;
}
